// =============================================================================
// domain/importDedupe.ts — the count-based import dedupe.
// -----------------------------------------------------------------------------
// PORTED FROM `tradeFingerprint` + `addImportedTradesDedupe` — semantics, not
// code (D3). The comments below are the old client's reasoning, kept because both
// problems it solves are ones a simpler dedupe gets wrong in opposite directions.
//
// ── WHY A SET IS NOT ENOUGH ──
//
// 1. ONE ORDER, MANY FILLS. A buy 10 closed by eight separate qty-1 sells pairs
//    into eight trades with IDENTICAL content fingerprints. A `Set`-based dedupe
//    silently drops seven of them — real trades, gone, with a toast saying
//    "skipped 7 duplicates".
//
// 2. THE SAME TRADES FROM TWO SOURCES. A fills CSV carries real broker fill ids;
//    an XLSX export carries synthetic ones. Any fingerprint including the fill id
//    differs, so the same execution imports twice and the book doubles.
//
// Counting satisfies both: M existing + N incoming with the same content yields
// max(M, N). Never dropped, never doubled.
//
// ── 🚨 THIS IS THE DEDUPE THE INGEST ENDPOINT DOES NOT HAVE ──
//
// The open duplicated-trades bug (docs/LOGGER-DUPLICATE-TRADES-2026-07-29.md) is
// exactly case 2: the Sierra study and the ATAS logger post the same execution
// under their own order ids, and `trade-ingest` keys idempotency on that id — so
// nothing collides and both rows land. This fingerprint deliberately EXCLUDES the
// external id for that reason. The import path has had the right rule all along;
// the ingest endpoint was written against a different one.
//
// ── 🚨 AND IT HAD ONE THING WRONG, FIXED 2026-08-09 ──
//
// The key was content-ONLY, which made two prop accounts copy-trading the same
// strategy indistinguishable — so importing them as separate per-account files
// silently dropped every account after the first. `accountId` is now in the key.
// The full reasoning, the measured behaviour and the trade-off are on
// `tradeFingerprint`; the review that found it is docs/INGEST-REVIEW-2026-08-09.md.
// =============================================================================

/** The fields the fingerprint reads. Deliberately not a whole Trade. */
export interface FingerprintTrade {
  contract: string | null;
  date: string | null;
  timeIn: string | null;
  timeOut: string | null;
  side: string | null;
  qty: number | null;
  entry: number | null;
  exit: number | null;
  /** 🚨 IN THE KEY since 2026-08-09 — see `tradeFingerprint`. */
  accountId?: string | null;
}

/**
 * A trade's identity for import purposes: its content, **scoped to its account**.
 *
 * ── 🚨 `accountId` IS IN THE KEY, AND LEAVING IT OUT WAS SILENT DATA LOSS ──
 *
 * The rule this was ported from was content-only, and the reasoning written down
 * for it was: "accountId varies when the same file is routed to a different
 * account". True, and it misses the case that matters for this product's actual
 * user.
 *
 * **A prop trader copy-trading one strategy across several funded accounts
 * produces byte-identical rows on each.** Same instant, same size, same fills.
 * With a content-only key they are indistinguishable, so — measured, not
 * theorised:
 *
 *   both accounts in ONE file .............. 2 added ✅
 *   account 1's file, then account 2's ..... 0 added, 1 "skipped" ❌
 *   three accounts, one already stored ..... 1 added, 1 "skipped" ❌
 *
 * **Most prop firms export one file per account**, so the ordinary workflow was
 * the broken one, and every account after the first vanished with a toast saying
 * duplicates were skipped. The beta user's own book already carries six such
 * pairs — identical MES trades on two accounts, 2026-05-12 — which survived only
 * because they happened to arrive in a single file.
 *
 * ⚠ THE TWO CASES THE CONTENT-ONLY KEY EXISTS FOR ARE UNHARMED, because both are
 * SAME-ACCOUNT by construction:
 *   - one order filled in many clips, pairing into identical round trips
 *   - the same executions arriving from a fills CSV and from an XLSX
 * Neither involves two accounts, so scoping by account cannot weaken either.
 *
 * ⚠ THE TRADE-OFF, STATED: re-importing a file and routing it to a DIFFERENT
 * account than last time now produces duplicates rather than silence. That is
 * the right way round. **A duplicate is visible in the import preview and can be
 * removed; a missing trade is neither.**
 *
 * ⚠ An unassigned trade keys on `''`, so two unassigned copies of one execution
 * still collapse — the conservative answer for the case where routing failed.
 *
 * ⚠ Still EXCLUDES `netPnl` and every broker/fill id: fill ids vary by export
 * source, and netPnl varies with the fee schedule applied at import time.
 *
 * ⚠ Prices are QUANTIZED to 4 decimals. A CSV → float → string round trip moves
 * the last bit, and an un-quantized compare would call the same execution new.
 */
export function tradeFingerprint(t: FingerprintTrade): string {
  const q = (n: number | null): number =>
    n != null && Number.isFinite(Number(n)) ? Math.round(Number(n) * 10000) / 10000 : 0;
  return [
    t.accountId || '',
    t.contract || '',
    t.date || '',
    t.timeIn || '',
    t.timeOut || '',
    t.side || '',
    t.qty || 0,
    q(t.entry),
    q(t.exit),
  ].join('|');
}

export interface DedupeResult<T> {
  /** Genuinely new — not already represented in `existing`. */
  added: T[];
  /** Incoming trades already represented. Counted, never silently dropped. */
  skipped: number;
}

/**
 * Which incoming trades are genuinely new.
 *
 * The algorithm, ported literally: count how many EXISTING trades carry each
 * fingerprint, then walk the incoming set decrementing that count. A decrement
 * means "already represented". Once a fingerprint's count reaches zero, further
 * incoming trades with the same content are new — which is the case where the
 * user genuinely took more identical trades since the last import.
 *
 * ⚠ ORDER-DEPENDENT BY DESIGN, and that is fine: which of N identical trades is
 * called "the new one" does not matter, only how many are added.
 */
export function dedupeIncoming<T extends FingerprintTrade>(
  incoming: readonly T[],
  existing: readonly FingerprintTrade[],
): DedupeResult<T> {
  const remaining = new Map<string, number>();
  for (const t of existing) {
    const fp = tradeFingerprint(t);
    remaining.set(fp, (remaining.get(fp) ?? 0) + 1);
  }

  const added: T[] = [];
  let skipped = 0;

  for (const t of incoming) {
    const fp = tradeFingerprint(t);
    const left = remaining.get(fp) ?? 0;
    if (left > 0) {
      // Already represented in the book. Consume one and move on.
      remaining.set(fp, left - 1);
      skipped++;
      continue;
    }
    added.push(t);
  }

  return { added, skipped };
}

/**
 * A human-readable summary, so "imported 0" is never ambiguous.
 *
 * ⚠ The all-duplicates case gets its OWN sentence. "Imported 0 new trades" reads
 * as a failure; "all 37 were already in your book" reads as the success it is,
 * and the difference decides whether a user re-tries the import or not.
 */
export function importSummary(result: DedupeResult<unknown>, fileName?: string): string {
  const n = result.added.length;
  const from = fileName ? ` from ${fileName}` : '';
  if (n === 0 && result.skipped > 0) {
    return `No new trades${from} — all ${result.skipped} were already in your book.`;
  }
  if (n === 0) return `No trades found${from}.`;
  const dup = result.skipped
    ? ` ${result.skipped} duplicate${result.skipped === 1 ? '' : 's'} skipped.`
    : '';
  return `Imported ${n} new trade${n === 1 ? '' : 's'}${from}.${dup}`;
}
